import React, { useState, useEffect } from "react"
import { Link } from "gatsby"

export default function NavItems({ isMobileMenu = false, onMenuItemClick = () => {} }) {
  const [selectedMenuItem, setSelectedMenuItem] = useState("")

  // Dismiss an open dropdown by clicking anywhere outside a menu item, or by
  // pressing Escape (expected keyboard behaviour for a disclosure menu). The
  // listeners are only attached while something is open. The effect runs after
  // the click that opened the menu has finished propagating, so that click can't
  // immediately re-close it.
  useEffect(() => {
    if (!selectedMenuItem) return

    const handleOutsideClick = e => {
      if (!e.target.closest(".has-submenu")) setSelectedMenuItem("")
    }
    const handleEscape = e => {
      if (e.key === "Escape") setSelectedMenuItem("")
    }
    document.addEventListener("click", handleOutsideClick)
    document.addEventListener("keydown", handleEscape)
    return () => {
      document.removeEventListener("click", handleOutsideClick)
      document.removeEventListener("keydown", handleEscape)
    }
  }, [selectedMenuItem])

  const subMenuHandler = key => {
    setSelectedMenuItem(key === selectedMenuItem ? "" : key)
  }

  // Navigating to a leaf page must also collapse the open submenu. NavItems lives
  // in the persistent header, so selectedMenuItem survives Gatsby's client-side
  // route change and (via the .is--active rule) would otherwise leave the dropdown
  // pinned open after you click through to a page.
  const handleItemClick = () => {
    setSelectedMenuItem("")
    onMenuItemClick()
  }

  // Top-level items that own a submenu are disclosure controls, not links: they
  // used to point at their own first child page (e.g. "Services" -> the same URL
  // as the "Offerings" child), which produced two links to one destination with
  // different text and failed WCAG 3.2.4 Consistent Identification. Rendering
  // them as buttons (role="button", keyboard operable, aria-expanded/controls)
  // removes the duplicate link entirely; navigation is via the submenu items.
  const keyHandler = (key, e) => {
    if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
      e.preventDefault()
      subMenuHandler(key)
    }
  }

  const pathname = typeof window !== "undefined" ? window.location.pathname : ""

  const parentToggle = (key, sectionMatch) => ({
    role: "button",
    tabIndex: 0,
    className: "has-child" + (pathname.includes(sectionMatch) ? " active" : ""),
    "aria-haspopup": "true",
    "aria-expanded": selectedMenuItem === key,
    "aria-controls": `submenu-${key}`,
    onClick: () => subMenuHandler(key),
    onKeyDown: e => keyHandler(key, e),
  })

  return (
    <>
      <li>
        <Link to="/" activeClassName="active" onClick={handleItemClick}>
          Home
        </Link>
      </li>
      <li
        class={`has-submenu menu-item-has-children ${
          selectedMenuItem === "about" ? `is--active` : ``
        }`}
      >
        <a {...parentToggle("about", "about-us/")}>About Us</a>
        <span class="plus" onClick={() => subMenuHandler("about")} aria-hidden={true}>
          <i className="fe-chevron-right"></i>
        </span>
        <ul
          id="submenu-about"
          class={`sub-menu ${selectedMenuItem === "about" ? `is--active` : ``}`}
        >
          <li>
            <Link to="/about-us/who-we-are" activeClassName="active" onClick={handleItemClick}>
              Who we are
            </Link>
          </li>
          <li>
            <Link to="/about-us/our-philosophy" activeClassName="active" onClick={handleItemClick}>
              Our Philosophy
            </Link>
          </li>
          <li>
            <Link to="/about-us/why-choose-us" activeClassName="active" onClick={handleItemClick}>
              Why choose us?
            </Link>
          </li>
          {/* <li>
            <Link
              to="/about-us/association-with-ifast"
              activeClassName="active"
              onClick={handleItemClick}
            >
              Association with iFast
            </Link>
          </li> */}
        </ul>
      </li>

      <li
        class={`has-submenu menu-item-has-children ${
          selectedMenuItem === "services" ? `is--active` : ``
        }`}
      >
        <a {...parentToggle("services", "services/")}>Services</a>
        <span class="plus" onClick={() => subMenuHandler("services")} aria-hidden={true}>
          <i className="fe-chevron-right"></i>
        </span>
        <ul
          id="submenu-services"
          class={`sub-menu ${selectedMenuItem === "services" ? `is--active` : ``}`}
        >
          <li>
            <Link
              to="/services/offerings"
              activeClassName="active"
              partiallyActive={true}
              onClick={handleItemClick}
            >
              Offerings
            </Link>
          </li>
          <li>
            <Link to="/services/client-journey" activeClassName="active" onClick={handleItemClick}>
              Client Journey
            </Link>
          </li>
          <li>
            <Link
              to="/services/client-engagement"
              activeClassName="active"
              onClick={handleItemClick}
            >
              Client Engagement
            </Link>
          </li>
          <li>
            <Link to="/services/fee-philosophy" activeClassName="active" onClick={handleItemClick}>
              Fee Philosophy
            </Link>
          </li>
        </ul>
      </li>

      <li
        class={`has-submenu menu-item-has-children ${
          selectedMenuItem === "clients" ? `is--active` : ``
        }`}
      >
        <a {...parentToggle("clients", "clients/")}>Clients</a>
        <span class="plus" onClick={() => subMenuHandler("clients")} aria-hidden={true}>
          <i className="fe-chevron-right"></i>
        </span>
        <ul
          id="submenu-clients"
          class={`sub-menu ${selectedMenuItem === "clients" ? `is--active` : ``}`}
        >
          <li>
            <Link to="/clients/business-owner" activeClassName="active" onClick={handleItemClick}>
              Business Owner
            </Link>
          </li>
          <li>
            <Link
              to="/clients/professional-salaried-employee"
              activeClassName="active"
              onClick={handleItemClick}
            >
              Professional
            </Link>
          </li>
          <li>
            <Link to="/clients/woman" activeClassName="active" onClick={handleItemClick}>
              Woman
            </Link>
          </li>
          <li>
            <Link to="/clients/retirees" activeClassName="active" onClick={handleItemClick}>
              Retirees
            </Link>
          </li>
        </ul>
      </li>

      <li
        class={`has-submenu menu-item-has-children ${
          selectedMenuItem === "resources" ? `is--active` : ``
        }`}
      >
        <a {...parentToggle("resources", "resources/")}>Resources</a>
        <span class="plus" onClick={() => subMenuHandler("resources")} aria-hidden={true}>
          <i className="fe-chevron-right"></i>
        </span>
        <ul
          id="submenu-resources"
          class={`sub-menu ${selectedMenuItem === "resources" ? `is--active` : ``}`}
        >
          {/* <li>
            <Link
              to="/resources/investment-letters"
              activeClassName="active"
              onClick={handleItemClick}
            >
              Investment Letters
            </Link>
          </li> */}
          <li>
            <Link
              to="/resources/blog"
              activeClassName="active"
              partiallyActive={true}
              onClick={handleItemClick}
            >
              Blog
            </Link>
          </li>
          <li>
            <Link
              to="/resources/media"
              activeClassName="active"
              partiallyActive={true}
              onClick={handleItemClick}
            >
              Media
            </Link>
          </li>
          <li>
            <Link
              to="/resources/tools"
              activeClassName="active"
              partiallyActive={true}
              onClick={handleItemClick}
            >
              Tools
            </Link>
          </li>
          <li>
            <Link
              to="/resources/book-summaries"
              activeClassName="active"
              partiallyActive={true}
              onClick={handleItemClick}
            >
              Book Summaries
            </Link>
          </li>
        </ul>
      </li>

      <li
        class={`has-submenu menu-item-has-children ${
          selectedMenuItem === "statutory" ? `is--active` : ``
        }`}
      >
        <a {...parentToggle("statutory", "statutory/")}>Statutory</a>
        <span class="plus" onClick={() => subMenuHandler("statutory")} aria-hidden={true}>
          <i className="fe-chevron-right"></i>
        </span>
        <ul
          id="submenu-statutory"
          class={`sub-menu ${selectedMenuItem === "statutory" ? `is--active` : ``}`}
        >
          <li>
            <Link to="/statutory/ria" activeClassName="active" onClick={handleItemClick}>
              RIA
            </Link>
          </li>
          <li>
            <Link to="/statutory/pms" activeClassName="active" onClick={handleItemClick}>
              PMS
            </Link>
          </li>
        </ul>
      </li>

      <li>
        <Link to="/contact-us" activeClassName="active" onClick={handleItemClick}>
          Contact Us
        </Link>
      </li>
    </>
  )
}
